import {
    Routes,
    Route,
    Outlet,
    Navigate,
} from 'react-router-dom';
import { WhenAuthenticated, WhenNoAuthenticated } from '@rainbow-modules/auth';
import { useVoxityLogin } from '@voxityai/auth/hooks';
import { auth } from 'data/firestore/firebase';
import Items from 'pages/Inbox/Items';
import Item from 'pages/Inbox/Item';
import AppLayout from 'layouts/App';
import Inbox from 'pages/Inbox';
import CallsInsights from 'pages/Insights/Calls';
import Billing from 'pages/Billing';
import History from 'pages/History';
import HistoryConversationDetails from 'pages/History/ConversationDetails';
import BlockedNumbers from 'pages/BlockedNumbers';
import InboxItemOverview from 'pages/Inbox/Item/Overview';
import InboxItemActivity from 'pages/Inbox/Item/Activity';
import CustomersPage from 'pages/Customers';
import CustomersListPage from 'pages/Customers/CustomersList';
import CustomerDetails from 'pages/Customers/CustomerDetails';
import CustomerDetailsActivity from 'pages/Customers/CustomerDetails/Activity';
import CustomerFiles from 'pages/Customers/CustomerDetails/CustomerFiles';
import TeamLayout from 'pages/Team';
import OpenTasksProvider from 'components/OpenTasksProvider';
import UserProvider from 'components/UserProvider';
import NotificationSettings from 'pages/Notifications';
import CompanyInfo from 'pages/CompanyInfo';
import PhoneNumbersPage from 'pages/PhoneNumbers';
import CalendarPage from 'pages/Calendar';
import CalendarViewPage from 'pages/Calendar/View';
import CalendarSettingsPage from 'pages/Calendar/Settings';
import CalendarViewList from 'pages/Calendar/View/List';
import CalendarEventStatus from 'pages/Calendar/Settings/EventStatus';
import CalendarEventTypesList from 'pages/Calendar/Settings/EventTypes/ListPage';
import CalendarEventTypeDetails from 'pages/Calendar/Settings/EventTypes/DetailsPage';
import CalendarsList from 'pages/Calendar/Settings/Calendars';
import NewCalendarPage from 'pages/Calendar/Settings/Calendars/CreatePage';
import EditCalendarPage from 'pages/Calendar/Settings/Calendars/EditPage';
import CalendarViewDay from 'pages/Calendar/View/Day';
import CalendarViewWeek from 'pages/Calendar/View/Week';
import TeamAffiliates from 'pages/Team/Affiliates';
import AffiliatesSettings from 'pages/Team/Affiliates/Settings';
import TeamPage from 'pages/Team/Members';
import EmailLogin from 'pages/EmailLogin';
import EmailSignUp from 'pages/EmailSignUp';
import AffiliateInsights from 'pages/Insights/Affiliate';
import Meta from 'pages/Social/Meta';
import MetaLoginCallback from 'pages/Social/Meta/callback';
import OpportunitiesPage from 'pages/Opportunities';
import OpportunitiesViewPage from 'pages/Opportunities/View';
import OpportunitiesViewList from 'pages/Opportunities/View/List';
import OpportunitiesViewBoard from 'pages/Opportunities/View/Board';
import OpportunitiesViewTodos from 'pages/Opportunities/View/Todos';
import LLMResponders from 'pages/LLMResponders';
import LLMRespondersListPage from 'pages/LLMResponders/RespondersList';
import LLMResponderDetails from 'pages/LLMResponders/ResponderDetails';
import OpportunityInsights from 'pages/Insights/Opportunities';
import TopicsInsights from 'pages/Insights/Topics';
import TasksPage from 'pages/Tasks';
import TasksList from 'pages/Tasks/List';
import ApiKeyPage from 'pages/ApiKeys';
import WebhooksV2Page from 'pages/WebhooksV2Page';
import WebhooksV2List from 'pages/WebhooksV2Page/WebhooksList';
import WebhookV2Details from 'pages/WebhooksV2Page/WebhookDetails';
import SkillsPage from 'pages/Skills';
import CustomerDetailsMemory from 'pages/Customers/CustomerDetails/Memory';
import SocialInsights from 'pages/Insights/Social';
import WebsiteChatbotsInsights from 'pages/Insights/WebsiteChatbots';
import BusinessHealthInsights from 'pages/Insights/BusinessHealth';
import WorkplacesPage from 'pages/Workplaces';
import WorkplacesListPage from 'pages/Workplaces/WorkplacesList';
import WorkplaceDetails from 'pages/Workplaces/WorkplaceDetails';
import WorkplaceCustomers from 'pages/Workplaces/WorkplaceDetails/Customers';
import WorkplaceDescription from 'pages/Workplaces/WorkplaceDetails/Description';
import WorkplaceCustomersOpportunitiesList from 'pages/Workplaces/WorkplaceDetails/Opportunities';
import WorkplaceNotes from 'pages/Workplaces/WorkplaceDetails/Notes';
import TagsManagementPage from 'pages/Tags';
import PipelinesPage from 'pages/Pipelines';
import ManagePipelinesPage from 'pages/Pipelines/Manage';
import EditPipeline from 'pages/Pipelines/Edit';
import EditPipelineGeneralInfo from 'pages/Pipelines/Edit/General';
import EditPipelineStages from 'pages/Pipelines/Edit/Stages';
import CustomerDetailsOpportunitiesList from 'pages/Customers/CustomerDetails/Opportunities';
import MemoryManagement from 'pages/MemoryManagement';
import TopicsManagementPage from 'pages/Topics';
import RemindersPage from 'pages/Reminders';
import ExternalAutomationsPage from 'pages/ExternalAutomations';
import ExternalAutomationsListPage from 'pages/ExternalAutomations/ExternalAutomationsList';
import ExternalAutomationDetailsPage from 'pages/ExternalAutomations/ExternalAutomationDetails';
import Executions from 'pages/ExternalAutomations/ExternalAutomationDetails/Executions';
import EditAditionalCompanyInfo from 'pages/EditAditionalCompanyInfo';
import CustomerDetailsPhoneNumbers from 'pages/Customers/CustomerDetails/CustomerPhones';
import { useEffect, useMemo } from 'react';
import { hideAppSpinner, showAppNotification, showAppSpinner } from '@rainbow-modules/app';
import MessagesPage from 'pages/Messages';
import ThreadMessages from 'pages/Messages/ThreadMessages';
import ConversationsSettingsPage from 'pages/Messages/Settings';
import ApiKeysSection from 'pages/Messages/Settings/ApiKeysSection';
import ChannelDeliverySection from 'pages/Messages/Settings/ChannelDeliverySection';
import useCheckBuildVersionUpdated from 'hooks/useCheckBuildVersionUpdated';
import InvoiceBuilderPage, { CustomerInvoicesTab } from 'customApps/invoiceBuilder';
import { CustomerNexhealthTab } from 'customApps/nexhealth';
import Analytics from 'analytics/analytics';

const App = () => {
    useCheckBuildVersionUpdated({
        onVersionChanged: () => {
            showAppNotification({
                title: 'New version available!!',
                description: 'We have released a new version of the app. Please refresh the page to get the latest version.',
                icon: 'info',
                timeout: 30000,
            });
        },
    });

    const redirectUri = useMemo(() => window.location.href, []);
    const { isLoading } = useVoxityLogin({
        auth,
        authServerUrl: process.env.REACT_APP_AUTH_SERVER_URL || '',
        redirectUri,
    });

    useEffect(() => {
        if (isLoading) {
            showAppSpinner();
        } else {
            hideAppSpinner();
        }
    }, [isLoading]);

    return (
        <Routes>
            <Route element={<Analytics />}>
                <Route
                    path="/login/email"
                    element={(
                        <WhenNoAuthenticated redirect="/">
                            <EmailLogin />
                        </WhenNoAuthenticated>
                    )}
                />
                <Route
                    path="/signup/email"
                    element={(
                        <WhenNoAuthenticated redirect="/">
                            <EmailSignUp />
                        </WhenNoAuthenticated>
                    )}
                />
                <Route
                    path="/"
                    element={(
                        <WhenAuthenticated path="/">
                            <UserProvider>
                                <OpenTasksProvider>
                                    <Outlet />
                                </OpenTasksProvider>
                            </UserProvider>
                        </WhenAuthenticated>
                    )}
                >
                    <Route path="/meta-callback" element={<MetaLoginCallback />} />
                    <Route path="/" element={<AppLayout />}>
                        <Route path=":agentId" element={<Outlet />}>
                            <Route path="tasks" element={<TasksPage />}>
                                <Route path="" element={<Navigate to="open" replace />} />
                                <Route path=":taskStatus" element={<TasksList />} />
                            </Route>
                            <Route path="inbox" element={<Inbox />}>
                                <Route path="" element={<Items />} />
                                <Route path=":itemId" element={<Item />}>
                                    <Route path="" element={<Navigate to="overview" replace />} />
                                    <Route path="overview" element={<InboxItemOverview />} />
                                    <Route path="activity" element={<InboxItemActivity />} />
                                </Route>
                            </Route>
                            <Route path="calendar" element={<CalendarPage />}>
                                <Route path="" element={<Navigate to="view" replace />} />
                                <Route path="view" element={<CalendarViewPage />}>
                                    <Route path="" element={<Navigate to="day" replace />} />
                                    <Route path="day" element={<CalendarViewDay />} />
                                    <Route path="week" element={<CalendarViewWeek />} />
                                    <Route path="list" element={<CalendarViewList />} />
                                </Route>
                                <Route path="create" element={<NewCalendarPage />} />
                                <Route path="edit/:calendarId" element={<EditCalendarPage />} />
                                <Route path="settings/event-types/:eventTypeId/details" element={<CalendarEventTypeDetails />} />
                                <Route path="settings" element={<CalendarSettingsPage />}>
                                    <Route path="" element={<Navigate to="event-types" replace />} />
                                    <Route path="event-types" element={<CalendarEventTypesList />} />
                                    <Route path="event-status" element={<CalendarEventStatus />} />
                                    <Route path="calendars" element={<CalendarsList />} />
                                </Route>
                            </Route>
                            <Route path="customers" element={<CustomersPage />}>
                                <Route path="" element={<CustomersListPage />} />
                                <Route path=":customerId" element={<CustomerDetails />}>
                                    <Route path="" element={<Navigate to="activity" replace />} />
                                    <Route path="activity" element={<CustomerDetailsActivity />} />
                                    <Route path="opportunities" element={<CustomerDetailsOpportunitiesList />} />
                                    <Route path="invoices" element={<CustomerInvoicesTab />} />
                                    <Route path="nexhealth" element={<CustomerNexhealthTab />} />
                                    <Route path="files" element={<CustomerFiles />} />
                                    <Route path="memory" element={<CustomerDetailsMemory />} />
                                    <Route path="phone-numbers" element={<CustomerDetailsPhoneNumbers />} />
                                </Route>
                            </Route>
                            <Route path="opportunities" element={<OpportunitiesPage />}>
                                <Route path="view/*" element={<Navigate to="" replace />} />
                                <Route path=":pipelineId" element={<Outlet />}>
                                    <Route path="" element={<Navigate to="view" replace />} />
                                    <Route path="view" element={<OpportunitiesViewPage />}>
                                        <Route path="" element={<Navigate to="board" replace />} />
                                        <Route path="list" element={<OpportunitiesViewList />} />
                                        <Route path="board" element={<OpportunitiesViewBoard />} />
                                        <Route path="todos" element={<OpportunitiesViewTodos />} />
                                    </Route>
                                </Route>
                            </Route>
                            <Route path="threads" element={<MessagesPage />}>
                                <Route path=":threadId" element={<Outlet />}>
                                    <Route path="" element={<Navigate to="messages" replace />} />
                                    <Route path="messages" element={<ThreadMessages />} />
                                </Route>
                            </Route>
                            <Route path="workplaces" element={<WorkplacesPage />}>
                                <Route path="" element={<WorkplacesListPage />} />
                                <Route path=":workplaceId" element={<WorkplaceDetails />}>
                                    <Route path="" element={<Navigate to="customers" replace />} />
                                    <Route path="customers" element={<WorkplaceCustomers />} />
                                    <Route path="opportunities" element={<WorkplaceCustomersOpportunitiesList />} />
                                    <Route path="notes" element={<WorkplaceNotes />} />
                                    <Route path="description" element={<WorkplaceDescription />} />
                                </Route>
                            </Route>
                            <Route path="insights" element={<Outlet />}>
                                <Route path="" element={<Navigate to="calls" replace />} />
                                <Route path="calls" element={<CallsInsights />} />
                                <Route path="affiliates" element={<AffiliateInsights />} />
                                <Route path="opportunities" element={<OpportunityInsights />} />
                                <Route path="social" element={<SocialInsights />} />
                                <Route path="website_chatbot" element={<WebsiteChatbotsInsights />} />
                                <Route path="topics" element={<TopicsInsights />} />
                                <Route path="business" element={<BusinessHealthInsights />} />
                            </Route>

                            {/* Control panel */}
                            <Route path="billing" element={<Billing />} />
                            <Route path="integrations/invoice-builder" element={<InvoiceBuilderPage />} />
                            <Route path="settings/blocked-numbers" element={<BlockedNumbers />} />
                            <Route path="settings/phone-numbers" element={<PhoneNumbersPage />} />
                            <Route path="settings/tags" element={<TagsManagementPage />} />
                            <Route path="settings/topics" element={<TopicsManagementPage />} />
                            <Route path="settings/company-info" element={<CompanyInfo />} />
                            <Route path="settings/company-info/edit-additional-info" element={<EditAditionalCompanyInfo />} />
                            <Route path="settings/voip/notifications" element={<NotificationSettings />} />
                            <Route path="settings/conversations" element={<ConversationsSettingsPage />}>
                                <Route path="" element={<Navigate to="api-keys" replace />} />
                                <Route path="api-keys" element={<ApiKeysSection />} />
                                <Route path="channel-settings" element={<ChannelDeliverySection />} />
                            </Route>
                            <Route path="team" element={<TeamLayout />}>
                                <Route path="members" element={<TeamPage />} />
                                <Route path="affiliates" element={<TeamAffiliates />} />
                                <Route path="affiliates/settings" element={<AffiliatesSettings />} />
                            </Route>
                            <Route path="memory" element={<MemoryManagement />} />

                            <Route path="external-automations" element={<ExternalAutomationsPage />}>
                                <Route path="" element={<ExternalAutomationsListPage />} />
                                <Route path=":externalAutomationId" element={<ExternalAutomationDetailsPage />}>
                                    <Route path="" element={<Navigate to="executions" replace />} />
                                    <Route path="executions" element={<Executions />} />
                                </Route>
                            </Route>

                            <Route path="voip/history" element={<History />} />
                            <Route path="voip/history/:sessionId" element={<HistoryConversationDetails />} />
                            <Route path="social/meta" element={<Meta />} />

                            {/* LLM Responders */}
                            <Route path="llms" element={<LLMResponders />}>
                                <Route path="" element={<LLMRespondersListPage />} />
                                <Route path=":responderId" element={<LLMResponderDetails />} />
                            </Route>

                            {/* AI Skills */}
                            <Route path="skills" element={<SkillsPage />} />

                            {/* Developer */}
                            <Route path="developer" element={<Outlet />}>
                                <Route path="api" element={<ApiKeyPage />} />
                                <Route path="webhooks" element={<WebhooksV2Page />}>
                                    <Route path="" element={<WebhooksV2List />} />
                                    <Route path=":webhookId" element={<WebhookV2Details />} />
                                </Route>
                                <Route path="reminders" element={<RemindersPage />} />
                            </Route>

                            {/* Pipelines */}
                            <Route path="pipelines" element={<PipelinesPage />}>
                                <Route path="" element={<ManagePipelinesPage />} />
                                <Route path=":pipelineId/edit" element={<EditPipeline />}>
                                    <Route path="" element={<Navigate to="general" replace />} />
                                    <Route path="general" element={<EditPipelineGeneralInfo />} />
                                    <Route path="stages" element={<EditPipelineStages />} />
                                </Route>
                            </Route>
                        </Route>
                    </Route>
                </Route>
            </Route>
        </Routes>
    );
};

export default App;
